import React, { useRef, useState } from 'react';
import { css } from 'styled-components';

import { color } from '../../../atoms/colors';

import Icon from '../../../molecules/Icon';
import GatsbyImage from '../../Image/GatsbyImage';
import CallToAction from '../../CallToAction';

import limitWordCount from '../../../utils/limitWordCount';
import useIsHovered from '../../../hooks/useIsHovered';

import {
  Container,
  Image,
  Asset,
  IconCircle,
  Body,
  Text,
  linkColorMap,
  buttonColorMap,
  Clickable,
} from './styles/index.styled';

const CardComponent = ({
  card: {
    theme,
    alignment,
    fontAwesomeIcon,
    thumbnail,
    badge,
    headingKicker,
    heading,
    subhead,
    truncateText,
    wordCount,
    callToAction,
    publishDate,
  },
  className = '',
  style = {},
}) => {
  const cardRef = useRef(null);
  const isHovered = useIsHovered(cardRef);

  const [xMousePosition, setXMousePosition] = useState(null);
  const [yMousePosition, setYMousePosition] = useState(null);

  const handleClick = e => {
    e.stopPropagation();
    if (xMousePosition !== e.clientX || yMousePosition !== e.clientY) {
      e.preventDefault();
    }
  };

  const handleMouseDown = e => {
    setXMousePosition(e.clientX);
    setYMousePosition(e.clientY);
    e.preventDefault();
  };

  return (
    <Container
      ref={cardRef}
      theme={theme || 'light'}
      cta={callToAction?.url}
      style={style}
      className={className}
    >
      {callToAction?.url && (
        <Clickable
          to={callToAction?.url?.replace('https://upkeep.com', '')}
          variant="Clickable"
          aria-label={heading}
          onMouseDown={e => handleMouseDown(e)}
          onClick={e => handleClick(e)}
        />
      )}
      {thumbnail && (
        <Image isHovered={isHovered} className="card-image">
          <GatsbyImage
            src={thumbnail?.image?.gatsbyImageData}
            alt={thumbnail?.image?.alt}
          />
        </Image>
      )}
      {fontAwesomeIcon && (
        <Asset alignment={alignment}>
          <IconCircle>
            <Icon
              id={fontAwesomeIcon}
              width="100%"
              height="100%"
              fillColor="#fff"
            />
          </IconCircle>
        </Asset>
      )}
      {headingKicker || heading || subhead || callToAction?.url ? (
        <Body
          theme={theme}
          fontAwesomeIcon={fontAwesomeIcon}
          className="c-card-body"
        >
          {badge || headingKicker || heading || subhead ? (
            <Text theme={theme || 'light'} alignment={alignment}>
              {badge && <p className="badge">{badge}</p>}
              {(headingKicker || publishDate) && (
                <div className="eyebrow-wrapper">
                  <p className="eyebrow">{headingKicker}</p>
                  <p className="publish-date">{publishDate}</p>
                </div>
              )}
              {heading && <p className="heading">{heading}</p>}
              {subhead && (
                <div
                  className="subhead"
                  dangerouslySetInnerHTML={{
                    __html:
                      truncateText && wordCount
                        ? limitWordCount(subhead, wordCount)
                        : subhead,
                  }}
                />
              )}
            </Text>
          ) : null}
          {callToAction?.url && (
            <CallToAction
              url={callToAction.url}
              variant={
                alignment === 'center'
                  ? buttonColorMap[theme || 'light']
                  : linkColorMap[theme || 'light']
              }
              size={alignment === 'center' && 'sm'}
              nextIcon={alignment !== 'center' && 'chevron-right'}
              classes={isHovered ? 'hovered' : ''}
              styles={css`
                width: fit-content;
                ${theme === 'blue-hover-gradient' &&
                css`
                  &.hovered {
                    color: ${color.darkBlue[1000]};
                    svg {
                      use {
                        fill: ${color.darkBlue[1000]} !important;
                      }
                    }
                  }
                `}
              `}
            >
              {callToAction?.label}
            </CallToAction>
          )}
        </Body>
      ) : null}
    </Container>
  );
};

export default CardComponent;
